import React, { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  fetchUserByEmail,
  updateUser,
  syncUser,
  UserCreate,
  User
} from "../utils/api";
import Button from "./ui/Button";

interface ProfileData {
  [key: string]: string;
}

const Profile: React.FC = () => {
  const { user } = useUser();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [formData, setFormData] = useState<ProfileData>({
    email: "",
    username: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    shippingAddress: "",
    bio: "",
  });
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState({
    cardViews: true,
    weeklyDigest: false,
    marketing: false,
  });
  const [status, setStatus] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  useEffect(() => {
    if (user) {
      const emailFromClerk = user.primaryEmailAddress?.emailAddress || "";
      setFormData((prev) => ({
        ...prev,
        email: emailFromClerk,
        username: emailFromClerk.split("@")[0] || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phoneNumber: user.primaryPhoneNumber?.phoneNumber || "",
        shippingAddress: "",
      }));

      // Fetch existing user profile
      loadUserProfile(emailFromClerk);
    }
  }, [user]);

  const loadUserProfile = async (email: string) => {
    try {
      const profile = await fetchUserByEmail(email);
      setUserProfile(profile);
      setFormData((prev) => ({
        ...prev,
        firstName: profile.firstName || prev.firstName,
        lastName: profile.lastName || prev.lastName,
        phoneNumber: profile.phoneNumber || prev.phoneNumber,
        shippingAddress: profile.shippingAddress || prev.shippingAddress,
        bio: profile.bio || prev.bio,
      }));
      // Load notification preferences from backend
      setNotifications({
        cardViews: profile.notifyCardViews !== false,
        weeklyDigest: profile.notifyWeeklyDigest === true,
        marketing: profile.notifyMarketing === true,
      });
    } catch (err) {
      // If profile doesn't exist, that's okay - we'll create it on first save
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      if (userProfile) {
        // Update existing user - include required fields + notification prefs
        const updateData = {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          shippingAddress: formData.shippingAddress,
          notifyCardViews: notifications.cardViews,
          notifyWeeklyDigest: notifications.weeklyDigest,
          notifyMarketing: notifications.marketing,
        };

        const updatedUser = await updateUser(userProfile.id, updateData);
        setUserProfile(updatedUser);
        setStatus({ message: "Profile saved.", type: "success" });
      } else {
        // Create new user
        const userData: UserCreate = {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          shippingAddress: formData.shippingAddress,
        };

        const newUser = await syncUser(userData);
        setUserProfile(newUser);
        setStatus({ message: "Profile created.", type: "success" });
      }

      // Update Clerk profile if needed
      if (user) {
        const clerkUpdate: Record<string, string> = {};
        if (formData.firstName !== user.firstName) {
          clerkUpdate.firstName = formData.firstName;
        }
        if (formData.lastName !== user.lastName) {
          clerkUpdate.lastName = formData.lastName;
        }

        if (Object.keys(clerkUpdate).length > 0) {
          try {
            await user.update(clerkUpdate);
          } catch (err) {
          }
        }
      }
    } catch (err: any) {
      setStatus({
        message: err.message || "Failed to save profile. Please try again.",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
      {/* Page header */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="eyebrow mb-1.5">Account</div>
          <h1 className="text-display text-3xl sm:text-4xl text-coffee-900 dark:text-cream-50">Profile</h1>
          <p className="mt-1.5 text-coffee-600 dark:text-cream-200">Contact details, preferences, and account settings.</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Status message */}
        {status && (
          <div className={`p-4 rounded-xl border text-sm ${status.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-700'
            }`}>
            {status.message}
          </div>
        )}

        {/* Section: Account info */}
        <div>
          <div className="eyebrow mb-3">Account info</div>
          <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 sm:p-8 shadow-coffee">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email */}
                <div className="md:col-span-2">
                  <label className="block eyebrow mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="you@example.com"
                  />
                  <p className="text-xs text-coffee-500 dark:text-cream-300 mt-1">
                    Linked to your account and used for card requests.
                  </p>
                </div>

                {/* Username */}
                <div className="md:col-span-2">
                  <label className="block eyebrow mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50 font-mono"
                    placeholder="yourhandle"
                  />
                  <p className="text-xs text-coffee-500 dark:text-cream-300 mt-1">
                    Your public handle on WhoBrew.
                  </p>
                </div>

                {/* First Name */}
                <div>
                  <label className="block eyebrow mb-2">
                    First name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="Jane"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block eyebrow mb-2">
                    Last name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="Smith"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block eyebrow mb-2">
                    Phone number
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Shipping Address */}
                <div className="md:col-span-2">
                  <label className="block eyebrow mb-2">
                    Shipping address
                  </label>
                  <input
                    type="text"
                    name="shippingAddress"
                    value={formData.shippingAddress || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="123 Main St, City, State 12345"
                  />
                  <p className="text-xs text-coffee-500 dark:text-cream-300 mt-1">
                    Used to ship physical NFC cards to you.
                  </p>
                </div>

                {/* Bio */}
                <div className="md:col-span-2">
                  <label className="block eyebrow mb-2">
                    Bio
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors resize-none bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="One or two sentences about yourself."
                    maxLength={200}
                  />
                  <p className="text-xs text-coffee-500 dark:text-cream-300 mt-1">
                    <span className="font-mono">{(formData.bio || "").length}/200</span> — appears on your public card.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-coffee-100 dark:border-coffee-800">
                <Button type="submit" variant="primary" size="md" loading={loading}>
                  {loading ? "Saving" : "Save changes"}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Section: Preferences */}
        <div>
          <div className="eyebrow mb-3">Preferences</div>
          <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 sm:p-8 shadow-coffee">
            <div className="space-y-0">
              <div className="flex items-center justify-between py-4 border-b border-coffee-100 dark:border-coffee-800">
                <div>
                  <p className="text-sm font-semibold text-coffee-900 dark:text-cream-50">Card view alerts</p>
                  <p className="text-xs text-coffee-600 dark:text-cream-200 mt-0.5">Get notified when someone views your card</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotifications(prev => ({ ...prev, cardViews: !prev.cardViews }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    notifications.cardViews ? 'bg-coffee-600' : 'bg-coffee-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                    notifications.cardViews ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between py-4 border-b border-coffee-100 dark:border-coffee-800">
                <div>
                  <p className="text-sm font-semibold text-coffee-900 dark:text-cream-50">Weekly analytics digest</p>
                  <p className="text-xs text-coffee-600 dark:text-cream-200 mt-0.5">A weekly summary of your card performance</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotifications(prev => ({ ...prev, weeklyDigest: !prev.weeklyDigest }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    notifications.weeklyDigest ? 'bg-coffee-600' : 'bg-coffee-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                    notifications.weeklyDigest ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between py-4">
                <div>
                  <p className="text-sm font-semibold text-coffee-900 dark:text-cream-50">Product updates</p>
                  <p className="text-xs text-coffee-600 dark:text-cream-200 mt-0.5">News about new features and improvements</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotifications(prev => ({ ...prev, marketing: !prev.marketing }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    notifications.marketing ? 'bg-coffee-600' : 'bg-coffee-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                    notifications.marketing ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
            <p className="text-xs text-coffee-500 dark:text-cream-300 mt-4 pt-4 border-t border-coffee-100 dark:border-coffee-800">
              Notification choices are saved with your profile.
            </p>
          </div>
        </div>

        {/* Section: Account details (meta) */}
        {userProfile && (
          <div>
            <div className="eyebrow mb-3">Account details</div>
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 sm:p-8 shadow-coffee">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-coffee-500 dark:text-cream-300 mb-1">User ID</p>
                  <span className="font-mono text-xs text-coffee-700 dark:text-cream-200 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded px-1.5 py-0.5">{userProfile.id}</span>
                </div>
                <div>
                  <p className="text-xs text-coffee-500 dark:text-cream-300 mb-1">Joined</p>
                  <span className="font-mono text-xs text-coffee-700 dark:text-cream-200">{new Date(userProfile.createdAt).toLocaleDateString()}</span>
                </div>
                <div>
                  <p className="text-xs text-coffee-500 dark:text-cream-300 mb-1">Last updated</p>
                  <span className="font-mono text-xs text-coffee-700 dark:text-cream-200">{new Date(userProfile.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section: Danger zone */}
        <div>
          <div className="eyebrow mb-3">Danger zone</div>
          <div className="bg-red-50/50 border border-red-200 rounded-2xl p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-coffee-900 dark:text-cream-50 mb-1">Delete account</p>
                <p className="text-sm text-coffee-600 dark:text-cream-200 max-w-md">
                  Permanently remove your account, card, and all data. This cannot be undone.
                </p>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  if (window.confirm("Are you sure? This will permanently delete your account and all data. This cannot be undone.")) {
                    // Account deletion — contact support to process
                    window.location.href = "mailto:support@whobrew.us?subject=Delete%20my%20account";
                  }
                }}
              >
                Delete account
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;
